import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useRecommendation, useSavedStatus, useTrackEvent } from '../hooks/useDiscovery';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/format';
import { RecommendationCard } from '../components/RecommendationCard';
import type { PublicRecommendation } from '../types';

function setMetaTags(title: string, description: string, image?: string, url?: string) {
  document.title = title;
  
  const updateMeta = (name: string, content: string, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector<HTMLMetaElement>(selector);
    if (!meta) {
      meta = document.createElement('meta');
      if (property) meta.setAttribute('property', name);
      else meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  updateMeta('description', description);
  updateMeta('og:title', title, true);
  updateMeta('og:description', description, true);
  updateMeta('og:type', 'article', true);
  if (url) updateMeta('og:url', url, true);
  if (image) updateMeta('og:image', image, true);
  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:title', title);
  updateMeta('twitter:description', description);
  if (image) updateMeta('twitter:image', image);
}

function clearMetaTags() {
  document.title = 'Los Recomendados';
}

export function RecommendationDetail() {
  const { id } = useParams<{ id: string }>();
  const { recommendation, loading, error } = useRecommendation(id!);
  const { user } = useAuth();
  const { saved, loading: savedLoading, setSaved } = useSavedStatus(id!);
  const { track } = useTrackEvent();
  const navigate = useNavigate();

  const [showSaveToast, setShowSaveToast] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (recommendation) {
      track({ type: 'view', recommendationId: id! });
      const title = `${recommendation.title} | Los Recomendados`;
      const description = recommendation.body?.slice(0, 160) || `Descubre ${recommendation.title} en Los Recomendados`;
      const image = recommendation.assets?.[0]?.url;
      const url = typeof window !== 'undefined' ? window.location.href : undefined;
      setMetaTags(title, description, image, url);
    }
    return () => clearMetaTags();
  }, [recommendation, id]);

  const handleSave = async () => {
    if (!user) {
      navigate(`/auth?next=/recommendations/${id}`);
      return;
    }

    setSaving(true);
    try {
      if (saved) {
        await window.discoveryApi.unsaveRecommendation(id!);
        setSaved(false);
        setSaveMessage('Eliminado de guardados');
      } else {
        await window.discoveryApi.saveRecommendation(id!);
        setSaved(true);
        setSaveMessage('Guardado en tus recomendaciones');
      }
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 3000);
    } catch (err) {
      setSaveMessage('No se pudo actualizar. Intenta de nuevo.');
      setShowSaveToast(true);
      setTimeout(() => setShowSaveToast(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const renderRelated = (related: PublicRecommendation[]) => {
    if (!related || related.length === 0) return null;
    return (
      <section className="mt-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">También te puede gustar</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {related.map((rec) => (
            <RecommendationCard key={rec.id} recommendation={rec} />
          ))}
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="container py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="skeleton h-8 w-1/3" />
          <div className="skeleton h-64 w-full rounded-xl" />
          <div className="skeleton h-6 w-3/4" />
          <div className="skeleton h-4 w-full" />
          <div className="skeleton h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (error || !recommendation) {
    return (
      <div className="container py-20 text-center">
        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">No se encontró la recomendación</h2>
        <p className="mt-2 text-gray-600">La recomendación que buscas no existe o no está disponible.</p>
        <Link to="/" className="mt-6 inline-block btn-primary">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const asset = recommendation.assets?.[0];
  const { counts } = recommendation;

  return (
    <article className="min-h-screen">
      {showSaveToast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg text-sm">
          {saveMessage}
        </div>
      )}

      {/* Hero Image */}
      <div className="relative h-96 sm:h-[600px] lg:h-[700px] w-full">
        {asset && asset.url ? (
          <img
            src={asset.url}
            alt={recommendation.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-8">
          <div className="max-w-4xl mx-auto px-4">
            {(recommendation.category || recommendation.city) && (
              <div className="flex flex-wrap gap-2 mb-3">
                {recommendation.category && (
                  <span className="px-3 py-1 rounded-full bg-white/90 text-sm font-medium text-gray-900">
                    {recommendation.category.name}
                  </span>
                )}
                {recommendation.city && (
                  <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur text-sm font-medium text-white">
                    {recommendation.city.name}
                  </span>
                )}
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
              {recommendation.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header Info */}
          <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-gray-100">
            {recommendation.business && (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3-2 2 2 2-2 2 2 2-2 3 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Recomendado por</p>
                  <p className="font-semibold text-gray-900">{recommendation.business.name}</p>
                </div>
              </div>
            )}

            <div className="flex-1" />

            <div className="flex items-center gap-6">
              {counts && (
                <div className="flex gap-4 text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.074 4.057-4.867 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    {counts.views}
                  </span>
                  <span className="text-gray-500 flex items-center gap-1">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    {counts.saves}
                  </span>
                </div>
              )}
              <div className="text-right">
                <p className="text-sm text-gray-500">Publicado</p>
                <p className="font-medium text-gray-900">{formatDate(recommendation.createdAt)}</p>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`btn ${saved ? 'btn-secondary' : 'btn-primary'} flex items-center gap-2`}
                aria-pressed={saved}
              >
                <svg className="w-5 h-5" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>
                  {saving
                    ? 'Guardando...'
                    : savedLoading
                      ? 'Verificando...'
                      : user
                        ? saved ? 'Guardado' : 'Guardar'
                        : 'Guardar'}
                </span>
              </button>
            </div>
          </div>

          {/* Body Content */}
          <div className="mb-12">
            {recommendation.body ? (
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-lg">
                {recommendation.body}
              </div>
            ) : (
              <p className="text-gray-500">Sin descripción.</p>
            )}
          </div>

          {/* Media Gallery */}
          {recommendation.assets && recommendation.assets.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Imágenes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendation.assets.map((assetItem, index) => (
                  <a
                    key={assetItem.id}
                    href={assetItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative aspect-video rounded-xl overflow-hidden block"
                  >
                    <img
                      src={assetItem.url}
                      alt={`${recommendation.title} - Imagen ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.074 4.057-4.867 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {renderRelated(recommendation.related ?? [])}

          <div className="pt-8 border-t border-gray-100 mt-16">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-900">
              ← Volver a explorar
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}